import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Dialog } from "@/components/ui/Dialog";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { showToast } from "@/lib/toast";
import { BRAND } from "@/lib/tokens";
import { t } from "@/lib/strings";
import { useAuth } from "@/hooks/useAuth";
import { useCreateSeries } from "@/hooks/useMeetingSeries";
import { workspacesUserCanCreateSeriesIn } from "@/lib/memoPermissions";
import { SeriesFormFields } from "@/components/memo/SeriesFormFields";
import type { MemoUser } from "@/lib/types";

const DEFAULT_SECTIONS = [
  "Performance Overview",
  "Projekte & Ausschreibungen",
  "Marketing & Aktionen",
  "Messen",
  "HR",
  "Technik & Produktion",
  "Aktuelles",
].join("\n");

const fieldLabel: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--ink-2)",
  marginBottom: 4,
  display: "block",
};
const input: React.CSSProperties = {
  width: "100%",
  fontSize: 13,
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--hairline)",
  background: "var(--surface)",
  color: "var(--ink-1)",
  boxSizing: "border-box",
};

export function NewSeriesDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const create = useCreateSeries();
  const { data: allWorkspaces = [] } = useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: () => api.listWorkspaces(),
  });
  // Only workspaces the user may actually create a series in (owner/admin, or
  // flow-admin). Prevents picking a workspace the backend would 403 on.
  const workspaces = useMemo(
    () => workspacesUserCanCreateSeriesIn(allWorkspaces, user),
    [allWorkspaces, user],
  );
  const [name, setName] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [weekday, setWeekday] = useState<number | null>(null);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [participants, setParticipants] = useState<MemoUser[]>([]);
  const [editors, setEditors] = useState<MemoUser[]>([]);

  const canSave = name.trim() && workspaceId && !create.isPending;

  const save = () => {
    if (!canSave) return;
    create.mutate(
      {
        home_workspace_id: workspaceId,
        name: name.trim(),
        section_template: sections
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        default_weekday: weekday,
        default_start_time: startTime,
        default_participant_ids: participants.map((u) => u.id),
        editor_user_ids: editors.map((u) => u.id),
      },
      {
        onSuccess: (series) => {
          showToast("Besprechungsreihe erstellt.", "success");
          onOpenChange(false);
          void navigate({ to: "/meetings/$seriesId", params: { seriesId: series.id } });
        },
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t.meetingsNewSeries} width={520}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 4 }}>
        <div>
          <label style={fieldLabel}>{t.seriesWorkspaceLabel}</label>
          <select
            style={input}
            value={workspaceId}
            onChange={(e) => setWorkspaceId(e.target.value)}
          >
            <option value="">…</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
        <SeriesFormFields
          name={name}
          onName={setName}
          sections={sections}
          onSections={setSections}
          weekday={weekday}
          onWeekday={setWeekday}
          startTime={startTime}
          onStartTime={setStartTime}
          participants={participants}
          onParticipants={setParticipants}
          editors={editors}
          onEditors={setEditors}
        />

        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          style={{
            alignSelf: "flex-end",
            background: BRAND.blue,
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "9px 16px",
            fontSize: 13,
            fontWeight: 600,
            cursor: canSave ? "pointer" : "not-allowed",
            opacity: canSave ? 1 : 0.6,
          }}
        >
          {t.meetingsNewSeries}
        </button>
      </div>
    </Dialog>
  );
}
